import useLocalStorage from "hooks/useLocalStorage";

/**
 * The "has this output already been committed, and has it changed since?" signal, split out of
 * `useCommitState` so the stage NAV (WorkingPanel's StageTabHeader — the "Up to date" / "N changes" chip
 * and the disabled state) can read it WITHOUT pulling in `useCommitState`'s dry-run/payload fetching,
 * which only makes sense while the Commit stage itself is mounted. `useCommitState` calls this same hook
 * internally, so both places agree on one piece of state rather than two copies of the same logic.
 *
 * Browser-local, same durability stopgap `methodology` already uses (see useCommitState's module
 * comment) — nothing here is written to the backend, so it is lost on a different browser/device.
 */
export function useCommitStatus({ sessionId, selectedOutputKey, outputCreatedCounter }) {
  const [lastCommit, setLastCommit] = useLocalStorage(
    `aiDataMapping.lastCommit.${sessionId}.${selectedOutputKey || "none"}`,
    null
  );
  // The one real signal available that the data behind this output may have moved since the last commit
  // into it: a new mapped output frame was produced (a re-map, or a second mapping) after that commit.
  const hasChangedSinceCommit = Boolean(lastCommit) && outputCreatedCounter !== lastCommit.outputCreatedCounter;
  const isUpToDate = Boolean(lastCommit) && !hasChangedSinceCommit;
  const navChip = isUpToDate ? "upToDate" : hasChangedSinceCommit ? "changed" : null;

  return { lastCommit, setLastCommit, isUpToDate, hasChangedSinceCommit, navChip };
}

export default useCommitStatus;
